/**
 * Request Tracker
 * 
 * Tracks failed API requests to prevent repeated calls to failing endpoints.
 * Implements a simple in-memory cache with automatic cleanup.
 */

interface FailedRequest {
  endpoint: string;
  failedAt: number;
  retryCount: number;
  isRetrying: boolean;
  lastError?: string;
  blockNotificationShown?: boolean; // Track if we've already shown the block notification
}

class RequestTracker {
  private failedRequests: Map<string, FailedRequest> = new Map();
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Start periodic cleanup of expired failures
    this.startCleanup();
  }

  /**
   * Start periodic cleanup of expired failed requests
   */
  private startCleanup() {
    // Clean up every 10 seconds
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredFailures();
    }, 10000);
  }

  /**
   * Stop the cleanup interval (for cleanup/testing)
   */
  public stopCleanup() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Remove expired failures from the tracker
   */
  private cleanupExpiredFailures() {
    const now = Date.now();
    const FAILURE_BLOCK_DURATION_MS = 30000; // 30 seconds

    for (const [endpoint, failure] of this.failedRequests.entries()) {
      if (now - failure.failedAt > FAILURE_BLOCK_DURATION_MS) {
        this.failedRequests.delete(endpoint);
      }
    }
  }

  /**
   * Check if a request to this endpoint should be blocked
   */
  public shouldBlockRequest(endpoint: string): boolean {
    const failure = this.failedRequests.get(endpoint);
    if (!failure) return false;

    const now = Date.now();
    const FAILURE_BLOCK_DURATION_MS = 30000; // 30 seconds

    // If the failure is expired, remove it and allow the request
    if (now - failure.failedAt > FAILURE_BLOCK_DURATION_MS) {
      this.failedRequests.delete(endpoint);
      return false;
    }

    // Block if we've already retried and failed
    return failure.retryCount > 0;
  }

  /**
   * Check if we should show the block notification for this endpoint
   * Only show it once per block period
   */
  public shouldShowBlockNotification(endpoint: string): boolean {
    const failure = this.failedRequests.get(endpoint);
    if (!failure) return false;

    // If we haven't shown the notification yet, show it and mark as shown
    if (!failure.blockNotificationShown) {
      failure.blockNotificationShown = true;
      return true;
    }

    return false;
  }

  /**
   * Check if a request is currently being retried
   */
  public isRetrying(endpoint: string): boolean {
    const failure = this.failedRequests.get(endpoint);
    return failure?.isRetrying || false;
  }

  /**
   * Mark a request as failed
   */
  public markFailed(endpoint: string, error?: string) {
    const existing = this.failedRequests.get(endpoint);
    
    if (existing) {
      // Increment retry count
      existing.retryCount += 1;
      existing.failedAt = Date.now();
      existing.lastError = error;
      existing.isRetrying = false;
      // Reset block notification flag when marking as failed again
      existing.blockNotificationShown = false;
    } else {
      // First failure
      this.failedRequests.set(endpoint, {
        endpoint,
        failedAt: Date.now(),
        retryCount: 0,
        isRetrying: false,
        lastError: error,
        blockNotificationShown: false,
      });
    }
  }

  /**
   * Mark a request as currently retrying
   */
  public markRetrying(endpoint: string) {
    const failure = this.failedRequests.get(endpoint);
    if (failure) {
      failure.isRetrying = true;
    }
  }

  /**
   * Mark a request as successful (clear failure tracking)
   */
  public markSuccess(endpoint: string) {
    this.failedRequests.delete(endpoint);
  }

  /**
   * Get the last error for an endpoint
   */
  public getLastError(endpoint: string): string | undefined {
    return this.failedRequests.get(endpoint)?.lastError;
  }

  /**
   * Get the retry count for an endpoint
   */
  public getRetryCount(endpoint: string): number {
    return this.failedRequests.get(endpoint)?.retryCount || 0;
  }

  /**
   * Clear all tracked failures (useful for testing/debugging)
   */
  public clearAll() {
    this.failedRequests.clear();
  }

  /**
   * Get all currently tracked failed requests (for debugging)
   */
  public getAll(): FailedRequest[] {
    return Array.from(this.failedRequests.values());
  }
}

// Singleton instance
export const requestTracker = new RequestTracker();

// Export for testing
export { RequestTracker };

